import React, { ReactNode, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  StarterTemplate,
  createStarterTemplate,
  updateStarterTemplate,
  deleteStarterTemplate,
  moveStarterTemplateUp,
  moveStarterTemplateDown,
  getStarterTemplatesByCategory,
  getStarterTemplatesByType,
} from "../firebase/starterTemplateService";
import { Timestamp } from "firebase/firestore";
import { withFirebaseRetry } from "../utils/retryUtils";
import { globalRateLimiter } from "../utils/rateLimiter";
import { starterTemplatesQuery, selectFeatured } from "../queries/contentQueries";

interface StarterTemplateContextType {
  starterTemplates: StarterTemplate[];
  featuredStarterTemplates: StarterTemplate[];
  loading: boolean;
  error: Error | null;
  refreshStarterTemplates: () => Promise<void>;
  addStarterTemplate: (
    template: Omit<StarterTemplate, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  editStarterTemplate: (
    id: string,
    template: Partial<Omit<StarterTemplate, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removeStarterTemplate: (id: string) => Promise<void>;
  moveUp: (id: string) => Promise<void>;
  moveDown: (id: string) => Promise<void>;
  getTemplatesByCategory: (category: string) => Promise<StarterTemplate[]>;
  getTemplatesByType: (
    type: "template" | "extension" | "tool" | "boilerplate"
  ) => Promise<StarterTemplate[]>;
}

const EMPTY: StarterTemplate[] = [];

// Data lives in the TanStack Query cache; the provider is kept so the app tree doesn't change.
export const StarterTemplateProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => <>{children}</>;

export const useStarterTemplates = (): StarterTemplateContextType => {
  const queryClient = useQueryClient();
  const { data, isPending, error, refetch } = useQuery(starterTemplatesQuery);
  const starterTemplates = data ?? EMPTY;
  const featuredStarterTemplates = useMemo(
    () => selectFeatured(starterTemplates),
    [starterTemplates]
  );

  const setStarterTemplates = (
    update: (prev: StarterTemplate[]) => StarterTemplate[]
  ) =>
    queryClient.setQueryData<StarterTemplate[]>(
      starterTemplatesQuery.queryKey,
      (prev) => update(prev ?? [])
    );

  const refreshStarterTemplates = async () => {
    await refetch();
  };

  const addStarterTemplate = async (
    template: Omit<StarterTemplate, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("starterTemplate-add")) {
      throw new Error(
        "Rate limit exceeded for starter template operations. Please try again later."
      );
    }

    const id = await withFirebaseRetry(async () =>
      createStarterTemplate(template)
    );

    setStarterTemplates((prev) => [
      ...prev,
      { ...template, id, createdAt: Timestamp.now(), updatedAt: Timestamp.now() },
    ]);

    return id;
  };

  const editStarterTemplate = async (
    id: string,
    template: Partial<Omit<StarterTemplate, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("starterTemplate-edit")) {
      throw new Error(
        "Rate limit exceeded for starter template operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => updateStarterTemplate(id, template));

    setStarterTemplates((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, ...template, updatedAt: Timestamp.now() }
          : item
      )
    );
  };

  const removeStarterTemplate = async (id: string): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("starterTemplate-remove")) {
      throw new Error(
        "Rate limit exceeded for starter template operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => deleteStarterTemplate(id));
    setStarterTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  const moveUp = async (id: string): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("starterTemplate-move")) {
      throw new Error(
        "Rate limit exceeded for starter template operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => moveStarterTemplateUp(id));

    // For move operations, we need to refresh to get the correct order
    await refreshStarterTemplates();
  };

  const moveDown = async (id: string): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("starterTemplate-move")) {
      throw new Error(
        "Rate limit exceeded for starter template operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => moveStarterTemplateDown(id));

    // For move operations, we need to refresh to get the correct order
    await refreshStarterTemplates();
  };

  const getTemplatesByCategory = async (
    category: string
  ): Promise<StarterTemplate[]> => {
    return await getStarterTemplatesByCategory(category);
  };

  const getTemplatesByType = async (
    type: "template" | "extension" | "tool" | "boilerplate"
  ): Promise<StarterTemplate[]> => {
    return await getStarterTemplatesByType(type);
  };

  return {
    starterTemplates,
    featuredStarterTemplates,
    loading: isPending,
    error: error as Error | null,
    refreshStarterTemplates,
    addStarterTemplate,
    editStarterTemplate,
    removeStarterTemplate,
    moveUp,
    moveDown,
    getTemplatesByCategory,
    getTemplatesByType,
  };
};
